<script lang="ts">
    import {getGridContext} from './grid.context';
    import {getInfiniteScrollContextIfExists} from './infinite-scroll.context';
    import {exists} from 'shared/utils/custom-utils';
    import {getLocalization} from 'src/core';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';

    export let additionalClasses = '';
    export let additionalContainerClasses = '';

    const {settings} = getGridContext();
    const infiniteScrollContext = getInfiniteScrollContextIfExists(); // There may be infinite scroll context in some parent components of the grid
    const gridElementRef$ = exists(infiniteScrollContext) ? infiniteScrollContext.gridElementRef$ : null;
    const infiniteScrollLoading$ = exists(infiniteScrollContext) ? infiniteScrollContext.loading$ : null;
    const localize = getLocalization();

    let gridContainerElement: HTMLElement;
    $: if (exists(infiniteScrollContext)
        && exists(gridElementRef$)
        && exists(gridContainerElement)
        && $gridElementRef$ !== gridContainerElement) infiniteScrollContext.setGridElementRef(gridContainerElement);

    $: infiniteScrollLoading = exists(infiniteScrollLoading$) ? $infiniteScrollLoading$ : false;
</script>

<div class="kp-generic-grid-container responsive bordered {additionalContainerClasses}">
    <div role="grid"
         tabindex="0"
         class="kp-generic-grid {additionalClasses}"
         bind:this={gridContainerElement}
         class:sticky-header={settings.stickyHeader}>

        {#if $$slots.header}
            <div class="header">
                <slot name="header"/>
            </div>
        {/if}

        <div class="body">
            <slot name="body"/>
        </div>

        {#if $$slots.footer}
            <div class="footer">
                <slot name="footer"/>
            </div>
        {/if}

        <!-- Infinite scroll loader -->
        {#if infiniteScrollLoading}
            <div class="infinite-scroll-loader"
                 role="progressbar"
                 aria-label={localize(/* @kp-localization commons.LoadingInProgress */ 'commons.LoadingInProgress')}>

                <KpLoadingBlock size="xs" fillAvailableSpace/>
            </div>
        {/if}
    </div>
</div>

<style lang="less">
    @import (reference) "styles/portaro.themes.less";
    @import (reference) "styles/portaro.variables.less";

    .kp-generic-grid-container {
        border: 1px solid @themed-border-muted;
        border-radius: @border-radius-default;
        width: 100%;
        --default-cell-z-index: 2;
        --header-cell-z-index-constant: 2;
        --pinned-columns-z-index-coefficient: 2;
        --active-cell-z-index-coefficient: 5;
        --default-grid-cell-padding: 5px;
        --condensed-grid-cell-padding: 3px;

        .kp-generic-grid {
            overflow-x: auto;
            width: 100%;
            max-width: 100%;
            margin-bottom: 0;

            &.sticky-header {
                .header {
                    background-color: @themed-body-bg;
                    position: sticky;
                    top: 0;
                    z-index: calc((var(--default-cell-z-index) + var(--header-cell-z-index-constant)) * var(--pinned-columns-z-index-coefficient));
                }
            }

            .header,
            .body,
            .footer {
                display: flex;
            }

            .footer {
                position: sticky;
                bottom: 0;
                left: 0;
                z-index: calc((var(--default-cell-z-index) + var(--header-cell-z-index-constant)) * var(--pinned-columns-z-index-coefficient));

                :global {
                    .kp-generic-grid-footer-header-cell {
                        border-top: 1px solid @themed-border-muted;
                    }
                }
            }

            .infinite-scroll-loader {
                width: 100%;
                height: 70px;
                margin-bottom: 16px;
                display: flex;
                flex-direction: column;
                flex-shrink: 0;
                overflow: hidden;
            }
        }
    }

    @keyframes infinite-scroll-loading {
        0% {
            width: 75%;
            left: -50%;
        }
        100% {
            width: 25%;
            left: 100%;
        }
    }
</style>